import {
  BuyerReservation,
  ReservationEligibility,
} from "@/features/pro-buyer-reservations/data/reservations-client";
import { useProductReservation } from "@/features/pro-buyer-reservations/hooks/useReservations";

/**
 * WHO-5947. What the product page shows about a reservation, for this viewer.
 *
 * - `none`: nothing (flag off, gateway flag off, or not reservable for them).
 * - `eligible`: a Pro Buyer who may see the "Reserve for 48 hours" button,
 *   possibly disabled (`blockedReason`).
 * - `requested`: the viewer's own request is waiting for the seller.
 * - `mine`: the piece is reserved for the viewer (fixed price, Buy now).
 * - `others`: the piece is reserved for another buyer.
 */
export type PdpReservationView =
  | { mode: "none" }
  | {
      mode: "eligible";
      eligibility: ReservationEligibility;
      blockedReason: "LIMIT_REACHED" | "COOLDOWN" | "ALREADY_REQUESTED" | null;
    }
  | { mode: "requested"; reservation: BuyerReservation }
  | { mode: "mine"; reservation: BuyerReservation }
  | { mode: "others"; reservedUntilDate: string };

const BLOCKING_REASONS = [
  "LIMIT_REACHED",
  "COOLDOWN",
  "ALREADY_REQUESTED",
] as const;

export const derivePdpReservationView = (
  state: ReturnType<typeof useProductReservation>,
  isOwner: boolean
): PdpReservationView => {
  const { myReservation, reservedUntilDate, eligibility } = state;

  if (myReservation?.status === "ACCEPTED") {
    return { mode: "mine", reservation: myReservation };
  }

  if (reservedUntilDate && !isOwner) {
    return { mode: "others", reservedUntilDate };
  }

  if (myReservation?.status === "REQUESTED") {
    return { mode: "requested", reservation: myReservation };
  }

  if (!eligibility || isOwner) {
    return { mode: "none" };
  }

  if (eligibility.allowed) {
    return { mode: "eligible", eligibility, blockedReason: null };
  }

  const blocking = BLOCKING_REASONS.find(
    reason => reason === eligibility.reason
  );

  return blocking
    ? { mode: "eligible", eligibility, blockedReason: blocking }
    : { mode: "none" };
};

export const usePdpReservation = (
  productId: string | null | undefined,
  isOwner: boolean
): PdpReservationView =>
  derivePdpReservationView(useProductReservation(productId), isOwner);
